// BookingForm: Customer info form before payment
import React, { useState, useEffect } from "react";
import { useBooking } from "../context/BookingContext";
import { useAuth } from "../context/AuthContext";
import CountryPhoneInput from "./CountryPhoneInput";
import { useSettings } from "../context/SettingsContext";

export default function BookingForm({ onSubmit, tour, initialData }) {
  const { travelers, setTravelers, setSelectedDate } = useBooking();
  const { user } = useAuth();
  const { settings } = useSettings();
  const cs = settings.currency_symbol || "$";
  const discount = Number(settings.discount_percentage) || 0;
  const maxTravelersAllowed = Number(settings.max_travelers_per_booking) || 20;

  // Priority: initialData > sessionStorage > user profile > defaults
  const getSavedData = () => {
    if (initialData) return initialData;
    if (!tour?.id) return null;
    try {
      const saved = sessionStorage.getItem(`booking_form_${tour.id}`);
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  };
  const savedData = getSavedData();

  const [customerName, setCustomerName] = useState(
    savedData?.customerName || user?.name || ""
  );
  const [email, setEmail] = useState(
    savedData?.email || user?.email || ""
  );
  const [phone, setPhone] = useState(
    savedData?.phone || user?.phone || "+212 "
  );
  const [errors, setErrors] = useState({});

  // Pre-fill travelers if coming back from confirm page or from sessionStorage
  React.useEffect(() => {
    if (initialData?.travelers && initialData.travelers !== travelers) {
      setTravelers(initialData.travelers);
    } else if (savedData?.travelers && !initialData) {
      setTravelers(savedData.travelers);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save form data to sessionStorage on every change
  useEffect(() => {
    if (tour?.id) {
      try {
        sessionStorage.setItem(
          `booking_form_${tour.id}`,
          JSON.stringify({ customerName, email, phone, travelers })
        );
      } catch (e) {
        // sessionStorage full or unavailable - ignore
      }
    }
  }, [customerName, email, phone, travelers, tour?.id]);

  // Date fixe (start_date du tour) - non modifiable
  const bookingDate = tour?.start_date ? tour.start_date.split("T")[0] : "";

  // Mettre à jour selectedDate dans le contexte quand le tour change
  useEffect(() => {
    if (bookingDate) {
      setSelectedDate(bookingDate);
    }
  }, [bookingDate, setSelectedDate]);

  // Calculer la date de fin
  const calculateEndDate = () => {
    if (!tour?.start_date || !tour?.duration_days) return null;

    const startDate = new Date(tour.start_date);
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + tour.duration_days - 1);

    return endDate.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Calculer le prix total
  const originalPrice = tour?.price || 0;
  const discountedPrice = discount > 0 ? originalPrice * (1 - discount / 100) : originalPrice;
  const totalPrice = discountedPrice * travelers;

  // Validation des champs
  const validateForm = () => {
    const newErrors = {};

    // Validation name
    if (!customerName.trim()) {
      newErrors.customerName = "Full name is required";
    } else if (customerName.trim().length < 2) {
      newErrors.customerName = "Name must be at least 2 characters";
    }

    // Validation email
    if (!email) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = "Invalid email format";
    }

    // Validation phone
    if (!phone) {
      newErrors.phone = "Phone number is required";
    } else if (!/^[\d\s+\-()]{8,25}$/.test(phone)) {
      newErrors.phone = "Invalid phone format";
    }

    // Validation travelers
    if (!travelers || travelers < 1) {
      newErrors.travelers = "At least 1 traveler required";
    } else if (travelers > maxTravelersAllowed) {
      newErrors.travelers = `Maximum ${maxTravelersAllowed} travelers allowed`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!tour?.id) {
      alert("Please select a tour first");
      return;
    }

    if (!bookingDate) {
      alert("This tour does not have a start date set");
      return;
    }

    // Valider tous les champs
    if (!validateForm()) {
      return;
    }

    const payload = {
      tourId: tour.id,
      customerName: customerName.trim(),
      email: email.trim(),
      phone: phone.trim(),
      date: bookingDate,
      travelers,
    };

    onSubmit?.(payload);
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-5 rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900"
    >
      {/* SECTION: Contact Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
          Contact Information
        </h3>

        {/* Full Name */}
        <div>
          <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
            Full Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            required
            value={customerName}
            onChange={(e) => {
              setCustomerName(e.target.value);
              setErrors({ ...errors, customerName: "" });
            }}
            placeholder="Adam Leo"
            className={`w-full rounded-lg border px-4 py-2.5 transition-colors focus:outline-none focus:ring-2 ${
              errors.customerName
                ? "border-red-500 focus:ring-red-500"
                : "border-slate-300 focus:border-emerald-500 focus:ring-emerald-500 dark:border-slate-700"
            } dark:bg-slate-800 dark:text-slate-100`}
          />
          {errors.customerName && (
            <p className="mt-1 text-sm text-red-500">{errors.customerName}</p>
          )}
        </div>

        {/* Email */}
        <div>
          <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
            Email Address <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setErrors({ ...errors, email: "" });
            }}
            placeholder="your.email@example.com"
            className={`w-full rounded-lg border px-4 py-2.5 transition-colors focus:outline-none focus:ring-2 ${
              errors.email
                ? "border-red-500 focus:ring-red-500"
                : "border-slate-300 focus:border-emerald-500 focus:ring-emerald-500 dark:border-slate-700"
            } dark:bg-slate-800 dark:text-slate-100`}
          />
          {errors.email && (
            <p className="mt-1 text-sm text-red-500">{errors.email}</p>
          )}
        </div>

        {/* Phone with Country Selector */}
        <CountryPhoneInput
          value={phone}
          onChange={setPhone}
          error={errors.phone}
          onClearError={() => setErrors({ ...errors, phone: "" })}
        />
      </div>

      {/* SECTION: Tour Details */}
      <div className="space-y-4 border-t border-slate-200 pt-5 dark:border-slate-700">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
          Tour Details
        </h3>

        {/* Date de réservation (FIXE - NON MODIFIABLE) */}
        <div>
          <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
            Tour Dates
          </label>
          <div className="rounded-lg border border-slate-300 bg-slate-50 px-4 py-2.5 dark:border-slate-700 dark:bg-slate-800">
            <div className="flex items-center gap-2">
              <svg
                className="h-5 w-5 text-slate-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <div className="flex-1">
                <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                  {bookingDate ? (
                    <>
                      {new Date(bookingDate).toLocaleDateString("en-US", {
                        weekday: "short",
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                      {calculateEndDate() && (
                        <span className="text-slate-500 dark:text-slate-400">
                          {" "}
                          → {calculateEndDate()}
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="text-slate-500">Date not set</span>
                  )}
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400">
                  {tour?.duration_days && `${tour.duration_days}-day tour`}
                </div>
              </div>
              <svg
                className="h-5 w-5 text-slate-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Number of Travelers */}
        <div>
          <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
            Number of Travelers <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            min={1}
            max={maxTravelersAllowed}
            required
            value={travelers || 1}
            onFocus={(e) => e.target.select()}
            onChange={(e) => {
              setTravelers(Number(e.target.value));
              setErrors({ ...errors, travelers: "" });
            }}
            className={`w-full rounded-lg border px-4 py-2.5 transition-colors focus:outline-none focus:ring-2 ${
              errors.travelers
                ? "border-red-500 focus:ring-red-500"
                : "border-slate-300 focus:border-emerald-500 focus:ring-emerald-500 dark:border-slate-700"
            } dark:bg-slate-800 dark:text-slate-100`}
          />
          {errors.travelers && (
            <p className="mt-1 text-sm text-red-500">{errors.travelers}</p>
          )}
        </div>
      </div>

      {/* SECTION: Price Summary */}
      <div className="space-y-3 rounded-lg bg-slate-50 p-4 dark:bg-slate-800/60">
        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-600 dark:text-slate-300">
            Price per person
          </span>
          <span className="font-medium text-slate-900 dark:text-slate-100">
            {discount > 0 ? (
              <span className="flex items-center gap-2">
                <span className="line-through text-slate-400">{cs}{originalPrice}</span>
                <span className="text-emerald-600 font-semibold">{cs}{discountedPrice.toFixed(2)}</span>
                <span className="text-xs bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 px-1.5 py-0.5 rounded-full font-semibold">-{discount}%</span>
              </span>
            ) : (
              <span>{cs}{originalPrice}</span>
            )}
          </span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-600 dark:text-slate-300">
            Number of travelers
          </span>
          <span className="font-medium text-slate-900 dark:text-slate-100">
            × {travelers || 1}
          </span>
        </div>
        <div className="border-t border-slate-200 pt-3 dark:border-slate-700">
          <div className="flex items-center justify-between">
            <span className="text-base font-semibold text-slate-900 dark:text-slate-100">
              Total Price
            </span>
            <span className="text-2xl font-bold text-emerald-600">
              {cs}{totalPrice.toFixed(2)}
            </span>
          </div>
          {discount > 0 && (
            <p className="text-xs text-right text-red-500 dark:text-red-400 mt-1">
              You save {cs}{((originalPrice * travelers) - totalPrice).toFixed(2)} with {discount}% discount!
            </p>
          )}
        </div>
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        className="w-full rounded-lg bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-4 font-semibold text-white transition-all duration-300 hover:from-emerald-700 hover:to-teal-700 hover:shadow-lg hover:shadow-emerald-500/25 focus:outline-none focus:ring-4 focus:ring-emerald-500/50 flex items-center justify-center gap-3"
      >
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        Continue to Payment
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
        </svg>
      </button>

      {/* Trust badges */}
      <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-slate-500">
        <div className="flex items-center gap-1.5">
          <svg className="h-4 w-4 text-emerald-500" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z" />
          </svg>
          <span>Secure payment</span>
        </div>
        <div className="flex items-center gap-1.5">
          <svg className="h-4 w-4 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>Instant confirmation</span>
        </div>
        <div className="flex items-center gap-1.5">
          <svg className="h-4 w-4 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>Free cancellation 48h</span>
        </div>
      </div>

      {/* Info légale */}
      <p className="text-center text-xs text-slate-500 dark:text-slate-400">
        By confirming, you agree to our{" "}
        <a href="#a" className="text-emerald-600 hover:underline">
          Terms & Conditions
        </a>
      </p>
    </form>
  );
}
